import { useState, useEffect, useCallback } from 'react';
import { ref, get } from 'firebase/database';
import { signInAnonymously } from 'firebase/auth';
import { database, auth } from '../config/firebase';

interface SurveyStats {
  totalResponses: number;
  responsesByQuestion: Record<number, Record<number, number>>; // questionIndex -> answerIndex -> count
  responsesByLanguage: Record<string, number>;
}

export function useSurveyResults() {
  const [stats, setStats] = useState<SurveyStats>({
    totalResponses: 0,
    responsesByQuestion: {},
    responsesByLanguage: {},
  });
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  // Manual refresh function for privacy-controlled updates
  const refreshData = useCallback(async () => {
    if (!authenticated) return;

    try {
      setLoading(true);
      const resultsRef = ref(database, 'results');
      const snapshot = await get(resultsRef);
      const data = snapshot.val();

      if (data) {
        const calculatedStats = calculateStatsFromRawData(data);
        setStats(calculatedStats);
      } else {
        setStats({
          totalResponses: 0,
          responsesByQuestion: {},
          responsesByLanguage: {},
        });
      }
    } catch (error) {
      console.error('Error fetching survey results:', error);
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  // Authenticate once on mount
  useEffect(() => {
    signInAnonymously(auth)
      .then(() => {
        setAuthenticated(true);
      })
      .catch((error) => {
        console.error('Authentication error:', error);
      });
  }, []);

  // Initial data load after authentication
  useEffect(() => {
    if (authenticated) {
      refreshData();
    }
  }, [authenticated, refreshData]);

  return { stats, loading, refreshData };
}

function calculateStatsFromRawData(entries: Record<string, any>): SurveyStats {
  const stats: SurveyStats = {
    totalResponses: 0,
    responsesByQuestion: {},
    responsesByLanguage: {},
  };

  // Process each entry (like the original belvedere implementation)
  Object.keys(entries).forEach((entryKey) => {
    const entry = entries[entryKey];

    if (entry.answers && Array.isArray(entry.answers)) {
      stats.totalResponses += 1;

      // Count language
      const lang = entry.lang || 'unknown';
      stats.responsesByLanguage[lang] =
        (stats.responsesByLanguage[lang] || 0) + 1;

      // Process answers array where index = question, value = answer choice index
      entry.answers.forEach(
        (answerChoiceIndex: number, questionIndex: number) => {
          if (!stats.responsesByQuestion[questionIndex]) {
            stats.responsesByQuestion[questionIndex] = {};
          }

          stats.responsesByQuestion[questionIndex][answerChoiceIndex] =
            (stats.responsesByQuestion[questionIndex][answerChoiceIndex] || 0) +
            1;
        }
      );
    }
  });

  return stats;
}
